import { EVENT_TYPE, RESOURCE_TYPE, UPDATE_FREQUENCY } from '/b2b/notifications/constants';
import React, { useCallback, useMemo, useState } from 'react';
import createSchema, {
    SCHEMA_OPTIONS,
    createHelperText,
    getPreferenceKeyPath,
    notificationDefaults,
} from './NotificationForm.schema';

import { Button } from '@mui/material';
import Locale from '/b2b/common/components/Locale';
import Portlet from '/b2b/common/components/Portlet';
import PortletContent from '/b2b/common/components/PortletContent';
import PortletFooter from '/b2b/common/components/PortletFooter';
import PropTypes from 'prop-types';
import ValidationForm from '/b2b/common/components/ValidationForm';
import castArray from 'lodash/castArray';
import get from 'lodash/get';
import makeStyles from '@mui/styles/makeStyles';
import set from 'lodash/set';
import styles from './NotificationForm.styles';

const useStyles = makeStyles(styles);

const formatPayload = params => {
    const { preferences, ...notificationSettings } = params || {};

    const prefsSchema = createSchema(
        Object.keys(SCHEMA_OPTIONS).filter(key => key.includes('.preferences.'))
    );
    const preferenceLookup = {};
    for (const [key, scheme] of Object.entries(prefsSchema)) {
        const [resourceType, eventType, preference] = getPreferenceKeyPath(key);
        const value = get(preferences, [resourceType, eventType, preference], undefined);
        if (value !== undefined) {
            // If a depends clause is present, check to see that the depends value validates
            if (scheme.depends) {
                let skip = false;
                for (const [dependsOn, ifIsValue] of Object.entries(scheme.depends)) {
                    const dependsValue = get(preferences, getPreferenceKeyPath(dependsOn), false);
                    if (!castArray(ifIsValue).includes(dependsValue)) {
                        // If the depends value is not valid, skip this preference
                        skip = true;
                    }
                }
                if (skip) {
                    continue;
                }
            }
            set(preferenceLookup, [resourceType, eventType, preference], value);
        }
    }
    const settingsArray = Object.entries(notificationSettings).reduce(
        (accumulator, [resourceType, value]) => {
            Object.entries(value).forEach(([eventType, frequency]) => {
                accumulator.push({
                    eventType,
                    resourceType,
                    frequency,
                    preferences: get(preferenceLookup, [resourceType, eventType], null),
                });
            });
            return accumulator;
        },
        []
    );

    const payload = settingsArray.reduce(
        (allSettings, { eventType, resourceType, frequency, preferences }) => {
            if (
                (frequency !== null && (!frequency || !UPDATE_FREQUENCY[frequency])) ||
                (!EVENT_TYPE[eventType] && !RESOURCE_TYPE[resourceType])
            ) {
                return allSettings;
            }
            if (preferences) {
                allSettings.push({
                    resourceType,
                    frequency,
                    preferences,
                });
            } else {
                allSettings.push({
                    resourceType,
                    frequency,
                });
            }

            return allSettings;
        },
        []
    );

    return payload;
};

const NotificationForm = props => {
    const { loading, onSubmit, permissions, values } = props;

    const classes = useStyles(props);

    const [isValid, setIsValid] = useState(false);
    const [canSubmit, setCanSubmit] = useState(isValid);

    const handleOnSubmit = values => {
        const payload = formatPayload(values);
        return onSubmit(payload);
    };

    const submitRef = React.useRef(handleOnSubmit);

    const schema = useMemo(() => createSchema(permissions), [permissions]);
    const helperText = useMemo(() => createHelperText(permissions), [permissions]);

    const handleValidate = useCallback(validation => {
        setCanSubmit(validation === undefined);
        setIsValid(!validation);
    }, []);

    const handleFieldChange = useCallback(
        (_field, value, oldValue) => {
            handleValidate(isValid || value !== oldValue);
        },
        [handleValidate, isValid]
    );

    const handleSettingsSubmit = useCallback(() => {
        return submitRef.current && submitRef.current();
    }, []);

    return (
        <Portlet className={classes.root}>
            <PortletContent>
                <ValidationForm
                    className={classes.form}
                    title="common.notifications"
                    submitButton={({ onClick }) => {
                        submitRef.current = onClick;
                        return null;
                    }}
                    schema={schema}
                    onSubmit={handleOnSubmit}
                    onFieldChange={handleFieldChange}
                    onValidate={handleValidate}
                    defaultValues={notificationDefaults}
                    values={values}
                    helperText={helperText}
                    isLoading={loading}
                />
            </PortletContent>
            <PortletFooter className={classes.portletFooter}>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={!isValid || !canSubmit}
                    onClick={handleSettingsSubmit}
                >
                    <Locale path="common.save" />
                </Button>
            </PortletFooter>
        </Portlet>
    );
};

NotificationForm.propTypes = {
    loading: PropTypes.bool,
    onSubmit: PropTypes.func,
    permissions: PropTypes.array,
    values: PropTypes.object,
};

export default NotificationForm;
